import { useState, useEffect, useMemo, useRef } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import MapPicker from '../../components/common/MapPicker';
import EpsgSelect from '../../components/common/EpsgSelect';
import Pagination from '../../components/common/Pagination';
import { PROJECTS_QUERY } from '../../graphql/queries';
import { CREATE_PROJECT_MUTATION, DELETE_PROJECT_MUTATION, REORDER_PROJECTS_MUTATION } from '../../graphql/mutations';
import styles from './Projects.module.css';
import Icon from '../../components/common/Icon';

const statusConfig = {
  active: { label: 'Active', className: 'badge-green' },
  archived: { label: 'Archived', className: 'text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded-full' },
  completed: { label: 'Completed', className: 'badge-blue' },
};

const formatStorage = (bytes: number): string => {
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const PAGE_SIZE = 20;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

type ProjectItem = {
  id: string;
  name: string;
  description?: string;
  location?: string;
  status: string;
  fileCount: number;
  storageUsed: number;
  updatedAt: string;
  thumbnailUrl?: string | null;
  members: Array<{ user: { id: string; firstName: string; lastName: string; avatar?: string } }>;
};

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = usePageParam();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if ((location.state as { openCreate?: boolean } | null)?.openCreate) {
      setCreateError('');
      setShowCreateModal(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const [newProject, setNewProject] = useState({ name: '', description: '', location: '', latitude: null as number | null, longitude: null as number | null, epsg: null as number | null });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [createError, setCreateError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const { data, loading, error, refetch } = useQuery(PROJECTS_QUERY, {
    variables: { page, limit: PAGE_SIZE, search: search || undefined, status: statusFilter || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const [createProject, { loading: creating }] = useMutation(CREATE_PROJECT_MUTATION, {
    onCompleted: (data) => {
      setShowCreateModal(false);
      setNewProject({ name: '', description: '', location: '', latitude: null, longitude: null, epsg: null });
      refetch();
      navigate(`/projects/${data.createProject.id}`);
    },
    onError: (err) => setCreateError(err.message),
  });

  const [deleteProject, { loading: deleting }] = useMutation(DELETE_PROJECT_MUTATION, {
    onCompleted: () => {
      setDeleteId(null);
      refetch();
    },
    onError: (err) => setDeleteError(err.message),
  });

  const openCreate = () => {
    setCreateError('');
    setShowCreateModal(true);
  };

  const askDelete = (id: string) => {
    setDeleteError('');
    setDeleteId(id);
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const changeStatus = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  // Layered over query data between a drop and the next server-sorted load.
  const [orderOverride, setOrderOverride] = useState<string[] | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ id: string; before: boolean } | null>(null);
  const [saveError, setSaveError] = useState('');
  const saveErrTimer = useRef<number | undefined>(undefined);

  const [reorderProjects] = useMutation(REORDER_PROJECTS_MUTATION, {
    onError: (err) => {
      setOrderOverride(null);
      setSaveError(`Saving order failed: ${err.message}`);
      if (saveErrTimer.current) window.clearTimeout(saveErrTimer.current);
      saveErrTimer.current = window.setTimeout(() => setSaveError(''), 7000);
    },
  });

  useEffect(() => {
    setOrderOverride(null);
  }, [search, statusFilter, page]);

  const loaded: ProjectItem[] = data?.projects?.projects || [];
  const total: number = data?.projects?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!loading && !error && page > totalPages) setPage(totalPages);
  }, [loading, error, page, totalPages]);

  // The server merges a sent order in front of the stored one, so reordering
  // a later page would lift its projects onto the first page.
  const canReorder = page === 1;
  const projects = useMemo(() => {
    if (!orderOverride) return loaded;
    const byId = new Map(loaded.map((p) => [p.id, p]));
    const ordered = orderOverride.flatMap((id) => byId.get(id) ?? []);
    const inOverride = new Set(orderOverride);
    return [...ordered, ...loaded.filter((p) => !inOverride.has(p.id))];
  }, [loaded, orderOverride]);

  const handleDragStart = (e: React.DragEvent<HTMLElement>, id: string) => {
    if (!canReorder || (e.target as HTMLElement).closest('button,a,input,select,textarea')) {
      e.preventDefault();
      return;
    }
    dragIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>, id: string, layout: 'grid' | 'list') => {
    if (!dragIdRef.current) return;
    e.preventDefault();
    if (dragIdRef.current === id) {
      setDropIndicator(null);
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    let before: boolean;
    if (layout === 'list') {
      before = e.clientY < rect.top + rect.height / 2;
    } else {
      const dx = (e.clientX - (rect.left + rect.width / 2)) / rect.width;
      const dy = (e.clientY - (rect.top + rect.height / 2)) / rect.height;
      before = Math.abs(dx) >= Math.abs(dy) ? dx < 0 : dy < 0;
    }
    setDropIndicator((cur) => (cur && cur.id === id && cur.before === before ? cur : { id, before }));
  };

  const handleDragEnd = () => {
    dragIdRef.current = null;
    setDraggingId(null);
    setDropIndicator(null);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropIndicator(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>, targetId: string) => {
    e.preventDefault();
    const dragId = dragIdRef.current;
    const before = dropIndicator && dropIndicator.id === targetId ? dropIndicator.before : true;
    handleDragEnd();
    if (!dragId || dragId === targetId) return;
    const ids = projects.map((p) => p.id);
    const from = ids.indexOf(dragId);
    if (from === -1) return;
    ids.splice(from, 1);
    const to = ids.indexOf(targetId);
    if (to === -1) return;
    ids.splice(before ? to : to + 1, 0, dragId);
    setOrderOverride(ids);
    reorderProjects({ variables: { projectIds: ids } });
  };

  const handleCreate = () => {
    if (!newProject.name.trim()) return;
    setCreateError('');
    createProject({ variables: { input: {
      name: newProject.name.trim(),
      description: newProject.description || undefined,
      location: newProject.location || undefined,
      latitude: newProject.latitude ?? undefined,
      longitude: newProject.longitude ?? undefined,
      epsg: newProject.epsg ?? undefined,
    } } });
  };

  return (
    <DashboardLayout onSearch={changeSearch} searchPlaceholder="Search projects...">
      <div className="space-y-6">
        <div className={`flex justify-between items-end ${styles.pageHeader}`}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Projects</h2>
            <p className="text-on-surface-variant mt-1">
              {total} projects total
            </p>
          </div>
          <div className={`flex items-center gap-3 ${styles.headerActions}`}>
            <div className="flex items-center gap-1 bg-surface-container-low rounded-lg p-1">
              {[
                { value: 'active', label: 'Active' },
                { value: 'completed', label: 'Completed' },
                { value: 'archived', label: 'Archived' },
                { value: '', label: 'All' },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => changeStatus(f.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    statusFilter === f.value
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex bg-surface-container-low rounded-lg p-1 gap-1">
              <button
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary' : 'text-on-surface-variant'}`}
              >
                <Icon name="grid_view" className="text-lg" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-primary' : 'text-on-surface-variant'}`}
              >
                <Icon name="list" className="text-lg" />
              </button>
            </div>
            <button
              onClick={openCreate}
              className="btn-primary px-5 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container"
            >
              <Icon name="add" className="text-sm" />
              New Project
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading projects...
          </div>
        ) : error ? (
          <div role="alert" className="p-4 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <Icon name="error" className="text-base" />
            Could not load your projects: {error.message}
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Icon name="folder_open" className="text-5xl text-outline mb-4" />
            <h3 className="text-lg font-bold text-on-surface mb-2">No projects found</h3>
            <p className="text-sm text-on-surface-variant mb-6">
              {search ? 'Try a different search term' : 'Create your first project to get started'}
            </p>
            <button onClick={openCreate} className="btn-primary px-6 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container">
              <Icon name="add" className="text-sm" />
              Create Project
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className={styles.projectsGrid}>
            {projects.map((project: ProjectItem) => (
              <div
                key={project.id}
                className={`${styles.projectCard} ${draggingId === project.id ? styles.cardDragging : ''} ${dropIndicator?.id === project.id ? (dropIndicator.before ? styles.cardDropBefore : styles.cardDropAfter) : ''}`}
                draggable={canReorder}
                onDragStart={(e) => handleDragStart(e, project.id)}
                onDragOver={(e) => handleDragOver(e, project.id, 'grid')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, project.id)}
                onDragEnd={handleDragEnd}
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className={styles.thumbnail}>
                  {project.thumbnailUrl
                    ? <img src={project.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    : <Icon name="domain" className="text-5xl text-primary/30" />
                  }
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-bold text-on-surface font-headline line-clamp-1">{project.name}</h3>
                    <span className={statusConfig[project.status as keyof typeof statusConfig]?.className}>
                      {statusConfig[project.status as keyof typeof statusConfig]?.label}
                    </span>
                  </div>
                  {project.location && (
                    <p className="text-xs text-on-surface-variant flex items-center gap-1 mb-3">
                      <Icon name="location_on" className="text-sm" />
                      {project.location}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-on-surface-variant mt-3 pt-3 border-t border-outline-variant/10">
                    <span className="flex items-center gap-1">
                      <Icon name="description" className="text-sm" />
                      {project.fileCount} files
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="hard_drive" className="text-sm" />
                      {formatStorage(project.storageUsed)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex -space-x-2">
                      {project.members.slice(0, 3).map((m) => (
                        <div
                          key={m.user.id}
                          className="w-6 h-6 rounded-full bg-primary-container border-2 border-white flex items-center justify-center text-[8px] font-bold text-white"
                          title={`${m.user.firstName} ${m.user.lastName}`}
                        >
                          {m.user.firstName[0]}{m.user.lastName[0]}
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-on-surface-variant">{formatDate(project.updatedAt)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`bg-white rounded-xl ${styles.tableWrap}`}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low">
                  <th className="table-header">Project</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Files</th>
                  <th className="table-header">Storage</th>
                  <th className="table-header">Last Updated</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {projects.map((project: ProjectItem) => (
                  <tr
                    key={project.id}
                    className={`table-row group ${draggingId === project.id ? styles.rowDragging : ''} ${dropIndicator?.id === project.id ? (dropIndicator.before ? styles.rowDropBefore : styles.rowDropAfter) : ''}`}
                    draggable={canReorder}
                    onDragStart={(e) => handleDragStart(e, project.id)}
                    onDragOver={(e) => handleDragOver(e, project.id, 'list')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, project.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-primary/5 flex items-center justify-center text-primary">
                          {project.thumbnailUrl
                            ? <img src={project.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                            : <Icon name="domain" className="text-lg" />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-bold text-on-surface">{project.name}</p>
                          <p className="text-[10px] text-on-surface-variant">{project.location || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={statusConfig[project.status as keyof typeof statusConfig]?.className}>
                        {statusConfig[project.status as keyof typeof statusConfig]?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs">{project.fileCount}</td>
                    <td className="px-6 py-4 text-xs">{formatStorage(project.storageUsed)}</td>
                    <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(project.updatedAt)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => navigate(`/projects/${project.id}`)} className="p-2 hover:bg-primary/10 rounded text-primary transition-colors" title="Open">
                          <Icon name="open_in_new" className="text-lg" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); askDelete(project.id); }}
                          className="p-2 hover:bg-error/10 rounded text-error transition-colors"
                          title="Delete"
                          aria-label={`Delete ${project.name}`}
                        >
                          <Icon name="delete" className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && (
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="projects" variant="card" />
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
        footer={
          <>
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !newProject.name.trim()}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Project'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {createError && (
            <div role="alert" className="p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-base" />
              {createError}
            </div>
          )}
          <div className="space-y-1.5">
            <label htmlFor="new-project-name" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Project Name *</label>
            <input
              id="new-project-name"
              className="input-field"
              placeholder="e.g. Hudson Yards Phase 2"
              value={newProject.name}
              onChange={(e) => setNewProject((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="new-project-location" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Location Name</label>
            <input
              id="new-project-location"
              className="input-field"
              placeholder="e.g. New York City, NY"
              value={newProject.location}
              onChange={(e) => setNewProject((p) => ({ ...p, location: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Map Pin (optional)</p>
            <MapPicker
              lat={newProject.latitude}
              lng={newProject.longitude}
              onChange={(lat, lng) => setNewProject((p) => ({ ...p, latitude: lat, longitude: lng }))}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="new-project-epsg" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Coordinate System (EPSG)</label>
            <EpsgSelect id="new-project-epsg" value={newProject.epsg} onChange={(code) => setNewProject((p) => ({ ...p, epsg: code }))} />
            <p className="text-[11px] text-on-surface-variant">Optional — the coordinate reference system the point clouds use (e.g. survey data in British National Grid). Leave unset for local/indoor scans.</p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="new-project-description" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Description</label>
            <textarea
              id="new-project-description"
              className="input-field resize-none"
              rows={3}
              placeholder="Brief description of the project..."
              value={newProject.description}
              onChange={(e) => setNewProject((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Project"
        footer={
          <>
            <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={() => { setDeleteError(''); deleteProject({ variables: { id: deleteId } }); }}
              disabled={deleting}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-white hover:opacity-90 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      >
        {deleteError && (
          <div role="alert" className="mb-3 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <Icon name="error" className="text-base" />
            {deleteError}
          </div>
        )}
        <p className="text-sm text-on-surface-variant">
          Are you sure you want to delete this project? This action cannot be undone and all associated files will be removed.
        </p>
      </Modal>

      {saveError && (
        <div className={styles.saveErrorBanner}>
          <Icon name="error" className="text-lg" />
          {saveError}
        </div>
      )}
    </DashboardLayout>
  );
};

export default Projects;
